import { call, put, takeLatest, select } from 'redux-saga/effects';
import apiRequest from '@libs/apiRequest';
import { setSnack } from 'utilities/redux.actions';
import * as types from './redux.actionTypes';
import * as actions from './redux.actions';
import { API_ENDPOINTS, getErrorMessage } from './common';
import { selectPagination, selectFilters, selectSort } from './redux.selectors';

// Fetch Templates Saga
function* fetchTemplatesSaga() {
    try {
        const pagination = yield select(selectPagination);
        const filters = yield select(selectFilters);
        const sort = yield select(selectSort);

        const params = {
            limit: pagination.limit,
            offset: pagination.offset,
            search: filters.search || '',
            'sort-by': sort.sortBy,
            'sort-descending': sort.sortDescending
        };

        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.GET_ALL_TEMPLATES,
            method: 'GET',
            params
        });

        // API returns { data: [...], limit, offset, total }
        yield put(actions.fetchTemplatesSuccess({
            data: response.data.data || [],
            total: response.data.total || 0
        }));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch templates');
        yield put(actions.fetchTemplatesFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Fetch Template with Goals Saga
function* fetchTemplateWithGoalsSaga(action) {
    try {
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.GET_TEMPLATE_WITH_GOALS}/${action.payload}/with-goals`,
            method: 'GET'
        });

        // API returns the template object directly (not nested in data)
        const templateData = response.data;
        
        yield put(actions.fetchTemplateWithGoalsSuccess(templateData));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch template with goals');
        yield put(actions.fetchTemplateWithGoalsFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Create Template Saga
function* createTemplateSaga(action) {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.CREATE_TEMPLATE,
            method: 'POST',
            data: JSON.stringify(action.payload)
        });

        yield put(actions.createTemplateSuccess(response.data));
        yield put(actions.closeTemplateDialog());
        yield put(actions.fetchTemplates());
        yield put(setSnack({ message: 'Template created successfully', type: 'success' }));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to create template');
        yield put(actions.createTemplateFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Update Template Saga
function* updateTemplateSaga(action) {
    try {
        const { templateId, data } = action.payload;
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.UPDATE_TEMPLATE}/${templateId}`,
            method: 'PUT',
            data: JSON.stringify(data)
        });

        yield put(actions.updateTemplateSuccess(response.data));
        yield put(actions.closeTemplateDialog());
        yield put(actions.fetchTemplates());
        yield put(setSnack({ message: 'Template updated successfully', type: 'success' }));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to update template');
        yield put(actions.updateTemplateFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Delete Template Saga
function* deleteTemplateSaga(action) {
    try {
        yield call(apiRequest, {
            url: `${API_ENDPOINTS.DELETE_TEMPLATE}/${action.payload}`,
            method: 'DELETE'
        });

        yield put(actions.deleteTemplateSuccess(action.payload));
        yield put(actions.closeDeleteDialog());
        yield put(actions.fetchTemplates());
        yield put(setSnack({ message: 'Template deleted successfully', type: 'success' }));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to delete template');
        yield put(actions.deleteTemplateFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Fetch All Goals Saga
function* fetchAllGoalsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.GET_ALL_GOALS,
            method: 'GET'
        });

        // API returns { data: [...], limit, offset, total }
        yield put(actions.fetchAllGoalsSuccess(response.data.data || []));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch goals');
        yield put(actions.fetchAllGoalsFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Add Goal to Template Saga
function* addGoalToTemplateSaga(action) {
    try {
        const { templateId, goalData } = action.payload;
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.ADD_GOAL_TO_TEMPLATE}/${templateId}/goals/add`,
            method: 'POST',
            data: JSON.stringify(goalData)
        });

        yield put(actions.addGoalToTemplateSuccess(response.data));
        yield put(actions.closeGoalDialog());
        yield put(actions.fetchTemplateWithGoals(templateId));
        yield put(setSnack({ message: 'Goal added to template successfully', type: 'success' }));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to add goal');
        yield put(actions.addGoalToTemplateFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Update Template Goal Saga
function* updateTemplateGoalSaga(action) {
    try {
        const { templateId, goalId, data } = action.payload;
        
        const response = yield call(apiRequest, {
            url: `${API_ENDPOINTS.UPDATE_TEMPLATE_GOAL}/${templateId}/goals/${goalId}`,
            method: 'PUT',
            data: JSON.stringify(data)
        });

        yield put(actions.updateTemplateGoalSuccess(response.data));
        yield put(actions.closeGoalDialog());
        yield put(actions.fetchTemplateWithGoals(templateId));
        yield put(setSnack({ message: 'Goal updated successfully', type: 'success' }));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to update goal');
        yield put(actions.updateTemplateGoalFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
    }
}

// Remove Goal from Template Saga
function* removeGoalFromTemplateSaga(action) {
    try {
        const { templateId, goalId } = action.payload;
        yield call(apiRequest, {
            url: `${API_ENDPOINTS.REMOVE_GOAL_FROM_TEMPLATE}/${templateId}/goals/${goalId}`,
            method: 'DELETE'
        });

        yield put(actions.removeGoalFromTemplateSuccess());
        yield put(actions.closeDeleteGoalDialog());
        yield put(actions.fetchTemplateWithGoals(templateId));
        yield put(setSnack({ message: 'Goal removed from template successfully', type: 'success' }));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to remove goal');
        yield put(actions.removeGoalFromTemplateFailure(errorMessage));
        yield put(setSnack({ message: errorMessage, type: 'error' }));
        // Do NOT refetch template goals or update store on error
    }
}

// Fetch Departments Saga
function* fetchDepartmentsSaga() {
    try {
        const response = yield call(apiRequest, {
            url: API_ENDPOINTS.GET_DEPARTMENTS,
            method: 'GET'
        });

        yield put(actions.fetchDepartmentsSuccess(response.data.data || []));
    } catch (error) {
        const errorMessage = getErrorMessage(error, 'Failed to fetch departments');
        yield put(actions.fetchDepartmentsFailure(errorMessage));
    }
}

// Root Saga
export default function* kpiTemplateSaga() {
    yield takeLatest(types.FETCH_TEMPLATES, fetchTemplatesSaga);
    yield takeLatest(types.FETCH_TEMPLATE_WITH_GOALS, fetchTemplateWithGoalsSaga);
    yield takeLatest(types.CREATE_TEMPLATE, createTemplateSaga);
    yield takeLatest(types.UPDATE_TEMPLATE, updateTemplateSaga);
    yield takeLatest(types.DELETE_TEMPLATE, deleteTemplateSaga);
    yield takeLatest(types.FETCH_ALL_GOALS, fetchAllGoalsSaga);
    yield takeLatest(types.FETCH_DEPARTMENTS, fetchDepartmentsSaga);
    yield takeLatest(types.ADD_GOAL_TO_TEMPLATE, addGoalToTemplateSaga);
    yield takeLatest(types.UPDATE_TEMPLATE_GOAL, updateTemplateGoalSaga);
    yield takeLatest(types.REMOVE_GOAL_FROM_TEMPLATE, removeGoalFromTemplateSaga);
}
